import { ArticleResponse } from "@/types/article";
import { getTimeAgo } from "@/lib/componentutills";
import { CollectionResponse } from "@/types/collection";
import { SearchResponse, SearchStory } from "@/lib/types/pageBuilder";
import {
  API_BASE_URL,
  API_ENDPOINTS,
  REVALIDATION_TIMES,
  MEDIA_BASE_URL,
  VIDEO_BASE_URL,
  PANCHANGAM_API_BASE_URL,
  PANCHANGAM_ENDPOINTS,
  SITEMAP_API_BASE_URL,
  SECTION_AD_SLOT_NAMES,
  ARTICLE_AD_SLOT_NAMES,
} from "./constants";
import { ApiError, handleApiError } from "./errors";
import { validateSlug, validateStoryId } from "./validation";

// Server-only helper: dynamically loads local store (avoids bundling fs/path for client)
async function getStore() {
  if (typeof window !== 'undefined') return null;
  try {
    const mod = await import('./local-store/PageStore');
    return mod.getLocalStore();
  } catch {
    return null;
  }
}


/**
 * Normalize ad slot data from local store format to API response format.
 * Transforms `additionalSlots` into `slots` + `totalSlots` for multi-slot ads.
 */
function normalizeAdSlot(raw: Record<string, any>): Record<string, any> {
  if (!raw || !raw.isMultiple || !Array.isArray(raw.additionalSlots)) {
    return raw;
  }
  // Build slots array: primary slot + additional slots
  const primarySlot = { desktopAdCode: raw.desktopAdCode, mobileAdCode: raw.mobileAdCode };
  const slots = [primarySlot, ...raw.additionalSlots];
  return {
    ...raw,
    slots,
    totalSlots: slots.length,
  };
}

export interface StaticPageResponse {
  "static-page": {
    id: number;
    title: string;
    content: string;
    metadata: {
      "mime-type": string;
      header: boolean;
      footer: boolean;
    };
  };
}

export interface CustomUrlPageResponse {
  page: {
    id: number;
    title: string;
    content: string;
    metadata: {
      header: boolean;
      footer: boolean;
      'mime-type': string;
      seo?: {
        'meta-title'?: string;
        'meta-description'?: string;
        'meta-keywords'?: string;
      };
    };
    type: string;
    'status-code': number;
  } | null;
  'status-code'?: number;
}

export interface MenuGroupItem {
  id: number;
  title: string | null;
  url: string | null;
  "item-type": string | null;
  "section-slug": string | null;
  "section-name": string | null;
  "section-url": string | null;
  "collection-slug": string | null;
  "tag-slug": string | null;
  "parent-id": number | null;
  rank?: number;
  data?: {
    link?: string;
    color?: string;
  } | null;
}

export interface MenuGroup {
  id: number;
  slug: string;
  name: string;
  items: MenuGroupItem[];
}

export interface MenuGroupsResponse {
  "menu-groups": Record<string, MenuGroup>;
}

export interface PanchangamEntry {
  Panchangam_id: string;
  Panchangam_date: string;
  Tamilday: string;
  Tamilyearandmonth: string;
  NallaNeramKalai: string;
  NallaNeramMalai: string;
  RaaguKaalam: string;
  Yemmakandam: string;
  Kuligai: string;
  Thithi?: string;
  Natchatram?: string;
  Chandrashtam?: string;
  Soolam?: string;
  Yogam?: string;
  Parigaram?: string;
  MeshamRasiPalan?: string;
  RishabamRasiPalan?: string;
  MidhunamRasiPalan?: string;
  KadahamRasiPalan?: string;
  SimamRasiPalan?: string;
  KanniRasiPalan?: string;
  ThulamRasiPalan?: string;
  ViruchagammRasiPalan?: string;
  DanushuRasiPalan?: string;
  MagaramRasiPalan?: string;
  KumbamRasiPalan?: string;
  MenamRasiPalan?: string;
  [key: string]: string | undefined;
}

// ============================================================================
// REQUEST-LEVEL DEDUPLICATION CACHE
// Prevents the same API URL from being fetched multiple times during a single
// server render (e.g., generateMetadata + BuilderPage both calling same API).
// Uses in-flight promise deduplication: if the same URL is already being fetched,
// returns the same promise instead of making a new request.
// Cache auto-clears entries after 30 seconds to prevent stale data across requests.
// ============================================================================
const _apiDedupeCache = new Map<string, { promise: Promise<any>; timestamp: number }>();
const DEDUPE_TTL_MS = 30_000; // 30 seconds
const MAX_DEDUPE_ENTRIES = 50; // Safety cap (reduced — most fetches are no-store now)

function getDedupeKey(url: string, method?: string): string {
  return `${method || 'GET'}:${url}`;
}

/** Clear the in-memory dedupe cache — call after sync to ensure fresh data */
export function clearApiDedupeCache(): void {
  _apiDedupeCache.clear();
}

// Register dedupe cache clear on store reload (server-only)
if (typeof window === 'undefined') {
  import('./local-store/PageStore').then(mod => {
    if (mod.onStoreReload) {
      mod.onStoreReload(() => _apiDedupeCache.clear());
    }
  }).catch(() => {});
}

async function fetchJsonWithTimeout<T>(
  url: string,
  options: RequestInit & { next?: { revalidate?: number } } = {},
  timeoutMs: number = 15000
): Promise<T> {
  // Only deduplicate GET requests (POST/PUT/DELETE should always execute)
  const method = options.method?.toUpperCase() || 'GET';
  if (method === 'GET') {
    const key = getDedupeKey(url, method);
    const cached = _apiDedupeCache.get(key);

    // Return cached promise if it's still fresh
    if (cached && (Date.now() - cached.timestamp) < DEDUPE_TTL_MS) {
      return cached.promise as Promise<T>;
    }

    // Evict stale entries if cache is too large
    if (_apiDedupeCache.size >= MAX_DEDUPE_ENTRIES) {
      const now = Date.now();
      for (const [k, v] of _apiDedupeCache) {
        if (now - v.timestamp > DEDUPE_TTL_MS) _apiDedupeCache.delete(k);
      }
      // If still too large, clear oldest
      if (_apiDedupeCache.size >= MAX_DEDUPE_ENTRIES) {
        const oldest = _apiDedupeCache.keys().next();
        if (!oldest.done) _apiDedupeCache.delete(oldest.value);
      }
    }

    // Create new fetch promise and cache it
    const fetchPromise = _doFetch<T>(url, options, timeoutMs);
    _apiDedupeCache.set(key, { promise: fetchPromise, timestamp: Date.now() });

    // Clean up cache entry after TTL
    fetchPromise.catch(() => {}).finally(() => {
      setTimeout(() => {
        const entry = _apiDedupeCache.get(key);
        if (entry && entry.promise === fetchPromise) {
          _apiDedupeCache.delete(key);
        }
      }, DEDUPE_TTL_MS);
    });

    return fetchPromise;
  }

  return _doFetch<T>(url, options, timeoutMs);
}

async function _doFetch<T>(
  url: string,
  options: RequestInit & { next?: { revalidate?: number } } = {},
  timeoutMs: number = 15000
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new ApiError(`Request failed: ${response.status}`, response.status);
    }

    return (await response.json()) as T;
  } catch (error) {
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getArticleBySlug(slug: string): Promise<ArticleResponse[] | null> {
  try {
    const validatedSlug = validateSlug(slug);
    const data = await fetchJsonWithTimeout<ArticleResponse>(
      `${API_BASE_URL}${API_ENDPOINTS.STORIES_BY_SLUG}?slug=${encodeURIComponent(validatedSlug)}`,
      { cache: 'no-store' }
    );
    return [data];
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function getPanchangamData(date?: string): Promise<PanchangamEntry[] | null> {
  try {
    // Use local API route to avoid CORS issues
    const isBrowser = typeof window !== "undefined";
    let apiUrl = "/api/panchangam";

    if (!isBrowser) {
      const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";
      apiUrl = `${baseUrl}/api/panchangam`;
    }

    if (date) {
      apiUrl += `?date=${encodeURIComponent(date)}`;
    }

    const data = await fetchJsonWithTimeout<PanchangamEntry[]>(
      apiUrl,
      { cache: "no-store" }
    );

    // Handle error response from API route
    if (data && typeof data === "object" && !Array.isArray(data) && "error" in data) {
      return null;
    }

    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function searchStories(query: string, limit: number = 20, offset: number = 0): Promise<SearchResponse | null> {
  try {
    const searchParams = new URLSearchParams({
      q: query,
      sort: 'latest-published',
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const data = await fetchJsonWithTimeout<{ total: number; items: any[] }>(
      `${API_BASE_URL}${API_ENDPOINTS.ADVANCED_SEARCH}?${searchParams}`,
      { cache: 'no-store' }
    );

    // Transform advanced-search response to SearchResponse format
    return {
      results: {
        from: offset,
        size: limit,
        total: data.total || 0,
        fallback: false,
        stories: (data.items || []) as SearchStory[],
      },
      term: query,
    } as SearchResponse;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export interface SearchFilters {
  sections?: string[];
  authors?: string[];
  storyTypes?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export async function searchStoriesWithFilters(
  query: string,
  limit: number = 20,
  offset: number = 0,
  filters?: SearchFilters
): Promise<SearchResponse | null> {
  try {
    const searchParams = new URLSearchParams({
      q: query,
      sort: 'latest-published',
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (filters?.sections?.length) {
      filters.sections.forEach(section => searchParams.append('section', section));
    }
    if (filters?.authors?.length) {
      filters.authors.forEach(author => searchParams.append('author', author));
    }
    if (filters?.storyTypes?.length) {
      filters.storyTypes.forEach(type => searchParams.append('story-template', type));
    }
    if (filters?.dateFrom) {
      searchParams.append('published-after', new Date(filters.dateFrom).getTime().toString());
    }
    if (filters?.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      searchParams.append('published-before', toDate.getTime().toString());
    }

    const apiUrl = `${API_BASE_URL}${API_ENDPOINTS.ADVANCED_SEARCH}?${searchParams}`;

    const data = await fetchJsonWithTimeout<SearchResponse>(
      apiUrl,
      { cache: 'no-store' }
    );

    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function searchAuthorsPublic(search: string): Promise<unknown | null> {
  try {
    const validatedSearch = validateSlug(search);
    const metaApiBase = process.env.SECTION_META_API_BASE_URL || "https://builderapi.dinamani.com";
    const url = `${metaApiBase}/api/authors/public/search?search=${encodeURIComponent(validatedSearch)}`;

    const data = await fetchJsonWithTimeout<unknown>(url, {
      cache: 'no-store',
    });

    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function advancedSearchByAuthor(
  author: string,
  limit: number = 20,
  offset: number = 0
): Promise<unknown | null> {
  try {
    const validatedAuthor = validateSlug(author);
    const searchParams = new URLSearchParams({
      author: validatedAuthor,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const data = await fetchJsonWithTimeout<unknown>(
      `${API_BASE_URL}${API_ENDPOINTS.ADVANCED_SEARCH}?${searchParams}`,
      { cache: 'no-store' }
    );

    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}
// Advanced Search - Stories by Tag
export interface TagStoryItem {
  id: string;
  headline: string;
  slug: string;
  subheadline?: string;
  "hero-image-s3-key"?: string;
  "hero-image-metadata"?: {
    width?: number;
    height?: number;
    "mime-type"?: string;
  } | null;
  "published-at": number;
  "last-published-at"?: number;
  "first-published-at"?: number;
  "author-name"?: string;
  "read-time"?: number;
  sections?: Array<{
    id: number;
    slug: string;
    name: string;
    "display-name"?: string;
    "section-url"?: string;
  }>;
  tags?: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  seo?: {
    "meta-title"?: string;
    "meta-description"?: string;
  };
}

export interface AdvancedSearchResponse {
  total: number;
  items: TagStoryItem[];
}

export async function getStoriesByTag(
  tagName: string,
  limit: number = 20,
  offset: number = 0
): Promise<AdvancedSearchResponse | null> {
  try {
    const searchParams = new URLSearchParams({
      'tag-name': tagName,
      sort: 'latest-published',
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const apiUrl = `${API_BASE_URL}${API_ENDPOINTS.ADVANCED_SEARCH}?${searchParams}`;

    const data = await fetchJsonWithTimeout<unknown>(
      apiUrl,
      { cache: 'no-store' }
    );

    return data as AdvancedSearchResponse;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

// export async function getSectionBySlug(slug: string): Promise<any | null> {
//   try {
//     const validatedSlug = validateSlug(slug);
//     const data = await fetchJsonWithTimeout<any>(
//       `${API_BASE_URL}${API_ENDPOINTS.SECTIONS_BY_SLUG}?slug=${encodeURIComponent(validatedSlug)}`,
//       { next: { revalidate: REVALIDATION_TIMES.DEFAULT } }
//     );
//     return data;
//   } catch (error) {
//     handleApiError(error);
//     return null;
//   }
// }


/**
 * Get image URL with optional width parameter for CDN-level resizing
 * @param s3Key - The S3 key of the image
 * @param width - Optional width to resize image at CDN level (reduces bandwidth)
 */
export function getImageUrl(s3Key: string, width?: number): string {
  const baseUrl = `${MEDIA_BASE_URL}/${s3Key}`;
  // Add width parameter if specified (Quintype CDN supports ?w=WIDTH)
  if (width) {
    return `${baseUrl}?w=${width}`;
  }
  return baseUrl;
}

export function getVideoUrl(s3Key: string): string {
  return `${VIDEO_BASE_URL}/${s3Key}`;
}

export function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  const tamilMonths = ['ஜனவரி', 'பிப்ரவரி', 'மார்ச்', 'ஏப்ரல்', 'மே', 'ஜூன்', 'ஜூலை', 'ஆகஸ்ட்', 'செப்டம்பர்', 'அக்டோபர்', 'நவம்பர்', 'டிசம்பர்'];
  // Render in IST regardless of server timezone
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date).reduce<Record<string, string>>((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});

  const day = parseInt(parts.day, 10);
  const month = tamilMonths[parseInt(parts.month, 10) - 1];
  const year = parts.year;
  const hours = parseInt(parts.hour, 10);
  const minutes = parts.minute;
  const ampm = (parts.dayPeriod || '').toLowerCase().includes('p') ? 'pm' : 'am';

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm} IST`;
}

export function extractTextFromCards(cards: any[]): string[] {
  if (!cards || cards.length === 0) return [];

  const paragraphs: string[] = [];

  cards.forEach(card => {
    const elements = card['story-elements'] || [];
    elements.forEach((element: any) => {
      if (element.text) {
        // Remove HTML tags and extract text content
        const textWithoutTags = element.text
          .replace(/<p>/g, '')
          .replace(/<\/p>/g, '||SPLIT||')
          .replace(/<[^>]*>/g, '') // Remove any other HTML tags
          .trim();

        // Split by our marker and filter out empty strings
        const paragraphsFromElement = textWithoutTags
          .split('||SPLIT||')
          .map((p: string) => p.trim())
          .filter((p: string) => p.length > 0);

        paragraphs.push(...paragraphsFromElement);
      }
    });
  });

  return paragraphs;
}

export async function getRelatedStories(storyId: string): Promise<any | null> {
  try {
    const validatedStoryId = validateStoryId(storyId);
    const data = await fetchJsonWithTimeout<any>(
      `${API_BASE_URL}${API_ENDPOINTS.RELATED_STORIES}/${validatedStoryId}/related-stories?fields=headline,slug,last-published-at,subheadline,hero-image-s3-key,story-template`,
      { cache: 'no-store' }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

// Re-export getTimeAgo as formatTimeAgo for backward compatibility
export const formatTimeAgo = (timestamp: number): string => getTimeAgo(timestamp);

export async function getVideos(): Promise<any | null> {
  try {
    const data = await fetchJsonWithTimeout<any>(
      `${API_BASE_URL}${API_ENDPOINTS.VIDEOS}`,
      { next: { revalidate: REVALIDATION_TIMES.FREQUENT } }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function getTrendingStories(): Promise<any | null> {
  try {
    const data = await fetchJsonWithTimeout<any>(
      `${API_BASE_URL}${API_ENDPOINTS.TRENDING}`,
      { next: { revalidate: REVALIDATION_TIMES.FREQUENT } }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function getGalleries(): Promise<any | null> {
  try {
    const data = await fetchJsonWithTimeout<any>(
      `${API_BASE_URL}${API_ENDPOINTS.GALLERIES}`,
      { next: { revalidate: REVALIDATION_TIMES.FREQUENT } }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function getKaruthukKalamStories(): Promise<any | null> {
  try {
    const data = await fetchJsonWithTimeout<any>(
      `${API_BASE_URL}${API_ENDPOINTS.KARUTHUK_KALAM}`,
      { next: { revalidate: REVALIDATION_TIMES.FREQUENT } }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export interface TrendingTag {
  id: number;
  name: string;
  slug: string;
  count: number;
  'meta-description': string | null;
  'meta-title': string | null;
}

export async function getTrendingTags(period: string = 'day', limit: number = 10): Promise<TrendingTag[] | null> {
  try {
    const searchParams = new URLSearchParams({
      period,
      limit: limit.toString(),
    });

    const url = `${API_BASE_URL}${API_ENDPOINTS.TRENDING_TAGS}?${searchParams}`;

    const data = await fetchJsonWithTimeout<any>(
      url,
      { next: { revalidate: REVALIDATION_TIMES.FREQUENT } }
    );
    return data.tags || [];
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function getCollectionBySlug(slug: string): Promise<CollectionResponse | null> {
  try {
    const validatedSlug = validateSlug(slug);
    const data = await fetchJsonWithTimeout<CollectionResponse>(
      `${API_BASE_URL}${API_ENDPOINTS.COLLECTIONS}/${validatedSlug}`,
      { cache: 'no-store' }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

/**
 * Fetch collection data using /collections/ endpoint
 * Used for URLs like /collection/trending which map to API: /api/v1/collections/trending
 */
export async function getCollectionByPath(collectionPath: string): Promise<CollectionResponse | null> {
  try {
    const validatedPath = validateSlug(collectionPath);
    const data = await fetchJsonWithTimeout<CollectionResponse>(
      `${API_BASE_URL}/collections/${validatedPath}`,
      { cache: 'no-store' }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function getMenuGroups(): Promise<MenuGroupsResponse | null> {
  try {
    const isBrowser = typeof window !== "undefined";
    const url = isBrowser ? "/api/menu-groups" : `${API_BASE_URL}${API_ENDPOINTS.MENU_GROUPS}`;

    // Server-side: no-store since menus come from LocalPageStore (layout.tsx, /api/menu-groups)
    // This is only a fallback if LocalPageStore has no menus
    const data = await fetchJsonWithTimeout<MenuGroupsResponse>(url, {
      ...(isBrowser ? {} : { cache: 'no-store' }),
    });
    return data;
  } catch (error) {
    console.error("getMenuGroups failed:", error);
    handleApiError(error);
    return null;
  }
}

/**
 * Find parent section info by matching parent-id with item-id in menu-groups
 * @param parentId - The parent-id from the section object
 * @returns Parent section name, slug, and sectionUrl (path only, no domain), or null if not found
 */
export async function getParentSectionInfo(parentId: number | null): Promise<{ name: string; slug: string; sectionUrl: string } | null> {
  if (!parentId) return null;

  try {
    const menuGroups = await getMenuGroups();
    if (!menuGroups || !menuGroups["menu-groups"]) return null;

    // Search through all menu groups
    for (const groupKey of Object.keys(menuGroups["menu-groups"])) {
      const group = menuGroups["menu-groups"][groupKey];
      const items = group.items || [];

      // Search through all items to find matching id
      for (const item of items) {
        if (item.id === parentId) {
          // Get section-url and remove domain if present
          let sectionUrl = item["section-url"] || item["section-slug"] || "";
          if (sectionUrl.startsWith("http")) {
            try {
              const url = new URL(sectionUrl);
              sectionUrl = url.pathname;
            } catch {
              // If URL parsing fails, try to extract path manually
              sectionUrl = sectionUrl.replace(/^https?:\/\/[^\/]+/, "");
            }
          }
          // Ensure it starts with /
          if (sectionUrl && !sectionUrl.startsWith("/")) {
            sectionUrl = "/" + sectionUrl;
          }

          return {
            name: item["section-name"] || item.title || "",
            slug: item["section-slug"] || "",
            sectionUrl: sectionUrl
          };
        }
      }
    }

    return null;
  } catch (error) {
    console.error("getParentSectionInfo failed:", error);
    return null;
  }
}

export async function getStaticPage(pageId: string): Promise<StaticPageResponse | null> {
  try {
    const data = await fetchJsonWithTimeout<StaticPageResponse>(
      `${API_BASE_URL}${API_ENDPOINTS.STATIC_PAGES}/${pageId}`,
      { cache: 'no-store' }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

/**
 * Fetch Quintype static page content by URL path.
 * Uses /api/v1/custom-urls/{encoded-path} endpoint.
 * Path encoding: /t20worldcup/analysis → %2ft20worldcup%2fanalysis
 * Returns null on error, or { page: null } for non-existent pages.
 */
export async function getCustomUrlPage(urlPath: string): Promise<CustomUrlPageResponse | null> {
  try {
    const encoded = encodeURIComponent(urlPath);
    const data = await fetchJsonWithTimeout<CustomUrlPageResponse>(
      `${API_BASE_URL}/custom-urls/${encoded}`,
      { cache: 'no-store' }
    );
    return data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

// Ad Slots types and API
export interface AdSlotData {
  desktopAdCode: string | null;
  mobileAdCode: string | null;
   isMultiple?: boolean;
  slots?: Array<{ desktopAdCode: string | null; mobileAdCode: string | null }>;
  totalSlots?: number;
}

export interface AdSlotsResponse {
  requested: string[];
  found: string[];
  data: Record<string, AdSlotData>;
}

export async function getAdSlots(slotNames: string[]): Promise<AdSlotsResponse | null> {
  try {
    const store = await getStore();
    if (!store) return null;
    const data: Record<string, AdSlotData> = {};
    const found: string[] = [];

    for (const name of slotNames) {
      const slot = store.getAdSlot(name);
      if (slot) {
        data[name] = normalizeAdSlot(slot) as AdSlotData;
        found.push(name);
      }
    }

    return {
      requested: slotNames,
      found,
      data,
    };
  } catch (error) {
    console.error("[getAdSlots] Failed:", error);
    return null;
  }
}

// Get all AMP ad slots in one request
export async function getAmpAdSlots(): Promise<AdSlotsResponse | null> {
  const ampSlots = ["amp-top", "amp-btm", "amp-sticky", "amp-page-story-end-ad"];
  return getAdSlots(ampSlots);
}

// Helper function to clean and parse ad code from API response
export function parseAdCode(adCode: string | null): string | null {
  if (!adCode) return null;

  // Remove surrounding quotes if present
  let cleaned = adCode.trim();
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1);
  }

  // Unescape the string - handle Unicode escapes and other escape sequences
  cleaned = cleaned
    .replace(/\\u003C/gi, "<")
    .replace(/\\u003E/gi, ">")
    .replace(/\\u0026/gi, "&")
    .replace(/\\u0027/gi, "'")
    .replace(/\\u0022/gi, '"')
    .replace(/\\""/g, '"')
    .replace(/\\"/g, '"')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/""/g, '"');

  return cleaned;
}

export interface WebsiteSettingValue {
  valueType: 'boolean' | 'string' | 'number';
  value: boolean | string | number;
}

export interface WebsiteSettingsResponse {
  requested: string[];
  found: string[];
  data: Record<string, WebsiteSettingValue>;
}

export async function getWebsiteSettings(settings: string[]): Promise<WebsiteSettingsResponse | null> {
  try {
    const store = await getStore();
    if (!store) return null;
    const data: Record<string, WebsiteSettingValue> = {};
    const found: string[] = [];

    for (const slug of settings) {
      const setting = store.getWebsiteSetting(slug);
      if (setting) {
        data[slug] = setting as WebsiteSettingValue;
        found.push(slug);
      }
    }

    return {
      requested: settings,
      found,
      data,
    };
  } catch (error) {
    return null;
  }
}

// Single website setting response
export interface SingleWebsiteSettingResponse {
  slug: string;
  valueType: string;
  value: string | number | boolean;
}

// Get ad slot after article list count
export async function getAdSlotAfterArticleList(): Promise<number> {
  try {
    const store = await getStore();
    if (!store) return 3;
    const data = store.getWebsiteSetting('ad-slot-after-article-list');
    if (data?.value != null) {
      const num = Number(data.value);
      return isNaN(num) ? 3 : num;
    }
    return 3;
  } catch (error) {
    return 3; // Default fallback
  }
}

/**
 * Fetch sitemap data by dynamic path from Quintype API
 * @param path - The sitemap path (e.g., "sitemap.xml", "sitemap-static-pages.xml")
 * Returns XML string for the requested sitemap
 */
export async function getSitemapByPath(path: string): Promise<string | null> {
  const url = `${SITEMAP_API_BASE_URL}/sitemap/${path}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/xml, text/xml, */*',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const text = await response.text();
    return text;
  } catch {
    return null;
  }
}

/**
 * Fetch news sitemap data from Quintype API
 * Returns XML string for news sitemap
 */
export async function getNewsSitemapData(): Promise<string | null> {
  return getSitemapByPath('news_sitemap.xml');
}

// Ad Slot After Paragraph Setting
export interface AdSlotAfterParagraphResponse {
  slug: string;
  valueType: string;
  value: number;
}

export async function getAdSlotAfterParagraph(): Promise<AdSlotAfterParagraphResponse | null> {
  try {
    const store = await getStore();
    if (!store) return null;
    const data = store.getWebsiteSetting('ad-slot-after-paragraph');
    return data as AdSlotAfterParagraphResponse | null;
  } catch (error) {
    return null;
  }
}

// Article Ad Slot
export interface ArticleAdResponse {
  slug: string;
  desktopAdCode: string | null;
  mobileAdCode: string | null;
}

export async function getArticleAd(): Promise<ArticleAdResponse | null> {
  try {
    const store = await getStore();
    if (!store) return null;
    const data = store.getAdSlot('article-ad');
    return data as ArticleAdResponse | null;
  } catch (error) {
    return null;
  }
}

// Ad Slot After Stories Setting
export interface AdSlotAfterStoriesResponse {
  slug: string;
  valueType: string;
  value: number;
}

export async function getAdSlotAfterStories(): Promise<AdSlotAfterStoriesResponse | null> {
  try {
    const store = await getStore();
    if (!store) return null;
    const data = store.getWebsiteSetting('ad-slot-after-stories');
    return data as AdSlotAfterStoriesResponse | null;
  } catch (error) {
    return null;
  }
}


// Search/Section Page Ads (big, small, inline 728x90)
export async function getSearchPageAds(): Promise<AdSlotsResponse | null> {
  const slotNames = [
    SECTION_AD_SLOT_NAMES.BIG_AD,
    SECTION_AD_SLOT_NAMES.SMALL_AD,
    SECTION_AD_SLOT_NAMES.INLINE_728x90,
  ];
  return getAdSlots(slotNames);
}

// Ad slot with multiple slots support
export interface MultiSlotAdData {
  isMultiple: true;
  slots: Array<{
    desktopAdCode: string;
    mobileAdCode: string;
  }>;
  totalSlots: number;
}

export interface SingleSlotAdData {
  isMultiple?: false;
  desktopAdCode: string | null;
  mobileAdCode: string | null;
}

export type AdSlotDataExtended = SingleSlotAdData | MultiSlotAdData;

export interface AdSlotsResponseExtended {
  requested: string[];
  found: string[];
  data: Record<string, AdSlotDataExtended>;
}

// Search Page Ads with extended response (supports multiple slots)
export async function getSearchPageAdsExtended(): Promise<AdSlotsResponseExtended | null> {
  const slotNames = [
    SECTION_AD_SLOT_NAMES.BIG_AD,
    SECTION_AD_SLOT_NAMES.SMALL_AD,
    SECTION_AD_SLOT_NAMES.INLINE_728x90,
  ];

  try {
    const store = await getStore();
    if (!store) return null;
    const data: Record<string, AdSlotDataExtended> = {};
    const found: string[] = [];

    for (const name of slotNames) {
      const slot = store.getAdSlot(name);
      if (slot) {
        data[name] = normalizeAdSlot(slot) as AdSlotDataExtended;
        found.push(name);
      }
    }

    return {
      requested: slotNames,
      found,
      data,
    };
  } catch (error) {
    console.error("[getSearchPageAdsExtended] Failed:", error);
    return null;
  }
}

// Article Page Multi Ads (sidebar and inline ads)
export async function getArticlePageMultiAds(): Promise<AdSlotsResponse | null> {
  const slotNames = [
    ARTICLE_AD_SLOT_NAMES.BIG_AD_1,
    ARTICLE_AD_SLOT_NAMES.SMALL_1,
    ARTICLE_AD_SLOT_NAMES.BIG_AD_2,
    
    ARTICLE_AD_SLOT_NAMES.BIG_AD_3,
    ARTICLE_AD_SLOT_NAMES.ROS_INREAD,
        ARTICLE_AD_SLOT_NAMES.MULTIPLE_300X250,
  ];
  return getAdSlots(slotNames);
}

// Article Page Style Response
export interface ArticlePageStyleResponse {
  slug: string;
  valueType: string;
  value: 'infinite' | 'single';
}

export async function getArticlePageStyle(): Promise<ArticlePageStyleResponse | null> {
  try {
    const store = await getStore();
    if (!store) return null;
    const data = store.getWebsiteSetting('article-page-style');
    return data as ArticlePageStyleResponse | null;
  } catch (error) {
    return null;
  }
}
